import React from "react";
import { reportError } from "utils/reportError";
import { isInMemoryStorage } from "utils/blockedStorage";
import { reloadPage } from "./Browser";
import { CrashFallback } from "./CrashFallback";

// Catches an error thrown while rendering anything inside it, so that error cannot take
// the rest of the page down. With no boundary, React 18 removes the entire page on a
// render error and leaves the visitor a white screen.
//
// The usual cause is a part of the page that failed to download. Anything loaded with
// @loadable/component throws its ChunkLoadError on the render after the download fails,
// and that render runs inside loadable's own promise `.catch`. Uncaught, the error also
// escapes as an unhandled rejection, and Gatsby (gatsby/cache-dir/navigation.js:29)
// answers any unhandled "Loading chunk N failed" by reloading the page, with no limit: a
// chunk that never loads reloads it about twice a second, forever. Catching the error
// here stops both. See integration/lazy-chunk-failure.bug-repro.test.js.
//
// The render runs inside that `.catch` only because the app is hydrated with the legacy
// ReactDOM.hydrate, not Gatsby's own hydrateRoot: gatsby-plugin-loadable-components-ssr
// replaces it (its gatsby-browser.js, replaceHydrateFunction). A legacy root renders a
// setState from a promise callback synchronously, inside the setState call (the debugger,
// paused on the rejection against alpha 2026-10-27, stops in scheduleUpdateOnFiber's
// legacy-only flushSyncCallbacks). Under hydrateRoot, which React 19 leaves as the only
// choice, the same uncaught error would surface as a plain window error: a white page, but
// no Gatsby reload loop. These boundaries catch it either way.
//
// Props:
//   name                Where it is, for the error report ("support-chat", "page").
//   fallback            What to render instead. Defaults to nothing, which is right for a
//                       part of the page that is optional: losing the support chat or the
//                       Aurora glow should cost exactly that.
//   resetKey            When it changes, render the children again. The page boundary
//                       passes the pathname, so leaving the page clears the error.
//   reloadOnChunkError  For a ChunkLoadError, reload the page once instead of showing
//                       the fallback; most real failures are a timeout that a second try
//                       gets past. Only for boundaries whose children are the page.
//
// It does not catch errors in event handlers, timers or promises the app handles itself,
// because those never pass through React's render. OpenReplay records those from the
// browser's own error events.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, { componentStack }) {
    const { name, reloadOnChunkError } = this.props;
    const reloading = Boolean(reloadOnChunkError) && isChunkLoadError(error) && claimChunkReload();
    reportError(error, { where: name, componentStack, reloading });
    if (reloading) {
      // componentDidCatch runs before the browser paints, so the fallback never flashes.
      this.setState({ reloading: true });
      reloadPage();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, reloading: false });
    }
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) {
      return this.props.children;
    }
    if (reloading) {
      return null;
    }
    return this.props.fallback ?? null;
  }
}

// The boundary around every page and around App (gatsby-browser.js and gatsby-ssr.js):
// these children are the page, so a failure shows CrashFallback, which offers a reload.
const PageErrorBoundary = ({ name, resetKey, children }) => (
  <ErrorBoundary
    name={name}
    resetKey={resetKey}
    fallback={<CrashFallback />}
    reloadOnChunkError
  >
    {children}
  </ErrorBoundary>
);

// webpack names a failed JS download "ChunkLoadError"; mini-css-extract-plugin's failed
// CSS download is a plain Error whose message starts "Loading CSS chunk".
const isChunkLoadError = (error) =>
  error?.name === "ChunkLoadError" ||
  /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || "");

const CHUNK_RELOAD_KEY = "errorBoundary.chunkReloadAt";
// Longer than webpack's 120 s chunk timeout, so a download that times out again after
// the reload does not earn a second one.
const CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;

// True, and recorded, when this tab has not already reloaded for a ChunkLoadError in the
// last five minutes.
const claimChunkReload = () => {
  try {
    // The in-memory stand-in for blocked storage (utils/blockedStorage.js) forgets the
    // record on reload, so every page load would earn a reload: the loop this guards
    // against, one reload per page load. Treat it like no storage at all.
    if (isInMemoryStorage(window.sessionStorage)) {
      return false;
    }
    const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY));
    if (last && Date.now() - last < CHUNK_RELOAD_WINDOW_MS) {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    // No sessionStorage (blocked, and the stand-in not installed): with nowhere to record
    // the reload, a chunk that never loads would reload forever. Show the fallback instead.
    return false;
  }
};

export { ErrorBoundary, PageErrorBoundary, isChunkLoadError, CHUNK_RELOAD_KEY };
